import type { DepartureResponse } from "@services/departure/types";
import type { DepartureStatusDTO } from "./components/DepartureStatus/DepartureStatus.types";

const ANCHORAGE_CLEARANCE = {
  REQUESTED: "Requested",
  NOT_REQUESTED: "Not Requested",
};

const ATTACHED_DOCUMENTS = {
  CARGO_MANIFEST: "Cargo Manifest",
  PRESAILING_MASTER_DECLARATION: "Presailing Master Declaration",
  CUSTOMS_CLEARANCE: "Customs Clearance",
  PORT_CLEARANCE: "Port Clearance",
  ANCHORAGE_CLEARANCE: "Anchorage Clearance",
};

export function buildDepartureStatus(
  details?: DepartureResponse,
  status?: string,
): DepartureStatusDTO {
  return {
    vesselCallId: details?.vesselCallId,
    status: status,
    masterName: details?.masterName,
    nextPort: details?.nextPort.portName,
    finalDestination: details?.finalDestination?.portName,
    vesselCondition: details?.vesselCondition.conditionDesc,
    draftFore: details?.draftFore,
    draftAft: details?.draftAft,
    anchorageClearance: details?.requestAnchorageClearance
      ? ANCHORAGE_CLEARANCE.REQUESTED
      : ANCHORAGE_CLEARANCE.NOT_REQUESTED,
    remarks: details?.remarks,
    cargoManifest: {
      documentName: ATTACHED_DOCUMENTS.CARGO_MANIFEST,
      fileName: details?.cargoManifestDocumentName || "",
      documentUrl: details?.cargoManifestDocumentUrl || "",
    },
    presailingMasterDeclaration: {
      documentName: ATTACHED_DOCUMENTS.PRESAILING_MASTER_DECLARATION,
      fileName: details?.presailingMasterDeclarationDocumentName || "",
      documentUrl: details?.presailingMasterDeclarationDocumentUrl || "",
    },
    customsClearance: {
      documentName: ATTACHED_DOCUMENTS.CUSTOMS_CLEARANCE,
      fileName: details?.clearanceDocumentName || "",
      documentUrl: details?.clearanceDocumentUrl || "",
    },
    portClearance: {
      documentName: ATTACHED_DOCUMENTS.PORT_CLEARANCE,
      fileName: details?.portClearanceDocName || "port_clearance.pdf",
      documentUrl: details?.portClearanceUrl || "",
    },
    anchorageClearanceDocument: {
      documentName: ATTACHED_DOCUMENTS.ANCHORAGE_CLEARANCE,
      fileName: details?.anchorageClearanceName || "anchorage_clearance.pdf",
      documentUrl: details?.anchorageClearanceUrl || "",
    },
    additionalDocuments: (details?.additionalDocuments ?? []).map((document) => ({
      documentName: document.name || "",
      fileName: document.originalFileName || document.fileName || document.name || "",
      documentUrl: document.url || "",
      documentType: document.type || "",
      otherSpecifyType: document.specify || "",
      issueDate: document.issueDate,
      expiryDate: document.expiryDate,
      documentSummary: document.summary || "",
    })),
    isFirstVisit: !!details?.isFirstVisit,
    task: details?.task,
    notes: details?.notes,
    vesselId: details?.vesselId,
    submittedBy: details?.submittedBy,
    submittedByRole: details?.submittedByRole,
    hasCancelledDeparture: details?.hasCancelledDeparture ?? false,
    requestAnchorageClearance: details?.requestAnchorageClearance ?? false,
  };
}

export function normalizeRole(role: string): string {
  return role.toLowerCase().replace(/_/g, "-");
}

export function formatDateTime(dateString?: string | null): string {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy}\n${hh}:${min}`;
}
